import axios from 'axios';
import { getOrCreateDeviceId } from '@/services/device/deviceIdentity';
import { clearDeviceKey, getDeviceKey, storeDeviceKey } from '@/services/device/deviceCredential';
import {
  sendDeviceHeartbeatNow,
  startDeviceHeartbeat,
  stopDeviceHeartbeat,
} from '@/services/device/deviceHeartbeatService';
import {
  persistOperationalPolicy,
  readCachedOperationalPolicy,
  isRecentAllowedOperationalPolicy,
} from '@/services/device/deviceOperationalPolicy';
import { isBackendUnavailableError } from '@/services/connectivity/connectivityService';

function initialOperationalState() {
  const cached = readCachedOperationalPolicy();
  const policyEnabled = Boolean(cached?.secondaryBlockingEnabled);
  const cachedSecondary = policyEnabled && cached?.registered && !cached?.isPrimary;
  const keepRecentAccess = cachedSecondary && isRecentAllowedOperationalPolicy(cached);
  return {
    operationalAccessLoaded: Boolean(cached),
    operationalAccessAllowed: !cachedSecondary || keepRecentAccess,
    operationalAccessReason: keepRecentAccess
      ? (cached.operationalAccessReason || 'PRIMARY_CONNECTED')
      : (cachedSecondary ? 'SECONDARY_API_UNAVAILABLE' : null),
    operationalAccessCheckedAt: cached?.operationalAccessCheckedAt || null,
    lastOperationalRequestId: 0,
    secondaryBlockingEnabled: policyEnabled,
    continuityPolicy: cached?.continuityPolicy || 'LEGACY_PRIMARY_GUARD',
    restaurantOperationMode: cached?.restaurantOperationMode || 'CLOUD_MODE',
    operationEpoch: 1,
    localHub: null,
    localControlEnabled: false,
    localControlDiscovery: {
      state: 'NOT_CONFIGURED',
      profile: null,
      identity: null,
      url: null,
      paired: false,
      last_error: null,
    },
    localControlState: {
      connected: false,
      paired: false,
      operation_mode: 'CLOUD_MODE',
      operation_epoch: 1,
      cloud_consensus: 'INSUFFICIENT_INFORMATION',
      failover_candidate: false,
      connected_local_devices: 0,
      fresh_reporting_devices: 0,
      last_error: null,
    },
    shadowReadComparisons: [],
    currentDeviceIsPrimary: Boolean(cached?.isPrimary),
    primaryEffectiveStatus: 'UNKNOWN',
    blockedByDevicePolicy: cachedSecondary && !keepRecentAccess,
    blockingError: null,
    secondaryApiUnavailableSince: null,
    cachedDevice: cached ? {
      name: cached.currentDeviceName,
      is_enabled: Boolean(cached.isEnabled),
      is_primary: Boolean(cached.isPrimary),
    } : null,
    cachedCanManageDevices: Boolean(
      cached?.canManageDevices || (cached?.registered && cached?.isPrimary),
    ),
    cachedRegistered: Boolean(cached?.registered),
  };
}

function authConfig(rootState) {
  const token = rootState.partner_token;
  return { headers: { Authorization: `Bearer ${token}` } };
}

function errorMessage(error) {
  return error?.response?.data?.error || error?.message || 'No se pudo actualizar el estado del dispositivo.';
}

function replaceDevice(devices, updated) {
  const exists = devices.some((device) => device.id === updated.id);
  return exists
    ? devices.map((device) => (device.id === updated.id ? updated : device))
    : [updated, ...devices];
}

const OPERATIONAL_REQUEST_ID = '__ewu_operational_request_id';

function acceptOperationalPayload(state, payload = {}) {
  const incoming = Number(payload?.[OPERATIONAL_REQUEST_ID]);
  if (!Number.isFinite(incoming)) return true;
  if (incoming < Number(state.lastOperationalRequestId || 0)) return false;
  state.lastOperationalRequestId = incoming;
  return true;
}

export default {
  namespaced: true,
  state: () => ({
    ...initialOperationalState(),
    deviceId: null,
    identityLoaded: false,
    loaded: false,
    registered: initialOperationalState().cachedRegistered,
    device: initialOperationalState().cachedDevice,
    restaurantDevices: [],
    restaurantHasPrimaryDevice: false,
    userIsPartner: initialOperationalState().cachedCanManageDevices,
    presenceEnabled: false,
    deviceKeyPresent: false,
    heartbeatStarted: false,
    heartbeatLeader: false,
    lastHeartbeatSentAt: null,
    lastHeartbeatSucceededAt: null,
    heartbeatError: null,
    localReportedState: 'UNKNOWN',
    serverEffectiveState: 'UNKNOWN',
    primaryDevice: null,
    restaurantOperationalStatus: 'UNKNOWN',
    primaryAvailable: false,
    primaryConnected: false,
    operationalStatusCheckedAt: null,
    lastOperationalRequestId: 0,
    error: null,
  }),
  mutations: {
    setIdentity(state, deviceId) { state.deviceId = deviceId; state.identityLoaded = true; },
    setDeviceKeyPresent(state, value) { state.deviceKeyPresent = Boolean(value); },
    setCurrent(state, payload) {
      state.loaded = true;
      state.registered = Boolean(payload.registered);
      state.device = payload.device || null;
      state.restaurantHasPrimaryDevice = Boolean(payload.restaurant_has_primary_device);
      state.userIsPartner = Boolean(payload.can_manage_devices);
      state.presenceEnabled = Boolean(payload.presence_enabled);
      state.error = null;
      state.currentDeviceIsPrimary = Boolean(state.device?.is_primary);
      persistOperationalPolicy(state);
    },
    setDevices(state, payload) {
      state.restaurantDevices = payload.devices || [];
      state.restaurantHasPrimaryDevice = Boolean(payload.restaurant_has_primary_device);
      state.error = null;
    },
    removeDevice(state, payload = {}) {
      const removed = payload.device || {};
      const removedId = String(removed.id || payload.device_id || '');
      state.restaurantDevices = state.restaurantDevices.filter(
        (device) => String(device.id) !== removedId,
      );
      state.restaurantHasPrimaryDevice = Boolean(payload.restaurant_has_primary_device);
      if (state.device && String(state.device.id) === removedId) {
        state.registered = false;
        state.device = null;
        state.currentDeviceIsPrimary = false;
        state.deviceKeyPresent = false;
      }
      if (state.primaryDevice && String(state.primaryDevice.id) === removedId) {
        state.primaryDevice = null;
        state.primaryAvailable = false;
        state.primaryConnected = false;
        state.primaryEffectiveStatus = 'NO_PRIMARY';
        state.restaurantOperationalStatus = 'NO_PRIMARY';
      }
      state.error = null;
      persistOperationalPolicy(state);
    },
    applyMutation(state, payload) {
      state.restaurantDevices = replaceDevice(state.restaurantDevices, payload.device);
      state.restaurantHasPrimaryDevice = Boolean(payload.restaurant_has_primary_device);
      if (String(payload.device.device_uuid) === String(state.deviceId)) {
        state.registered = true;
        state.device = payload.device;
      }
      state.error = null;
      state.currentDeviceIsPrimary = Boolean(state.device?.is_primary);
      persistOperationalPolicy(state);
    },
    setUserIsPartner(state, value) { state.userIsPartner = Boolean(value); },
    setHeartbeatStarted(state, value) { state.heartbeatStarted = Boolean(value); },
    setHeartbeatLeader(state, value) { state.heartbeatLeader = Boolean(value); },
    setHeartbeatSentAt(state, value) { state.lastHeartbeatSentAt = value; },
    setLocalReportedState(state, value) { state.localReportedState = value || 'UNKNOWN'; },
    setHeartbeatSuccess(state, payload) {
      if (!acceptOperationalPayload(state, payload)) return;
      state.lastHeartbeatSucceededAt = payload.server_time || new Date().toISOString();
      state.heartbeatError = null;
      state.primaryDevice = payload.primary || state.primaryDevice;
      state.serverEffectiveState = payload.primary?.effective_status || 'NO_PRIMARY';
      state.restaurantOperationalStatus = payload.restaurant_operational_status || 'UNKNOWN';
      state.primaryAvailable = Boolean(payload.primary?.is_available);
      state.primaryConnected = Boolean(payload.primary?.is_connected);
      state.operationalStatusCheckedAt = payload.server_time || new Date().toISOString();
      if (payload.local_hub) state.localHub = payload.local_hub;
      applyOperationalServerState(state, {
        ...payload,
        current_device: payload.device ? { registered: true, ...payload.device } : undefined,
        primary_device: payload.primary,
      });
    },
    setHeartbeatError(state, value) { state.heartbeatError = value; },
    setLocalHubStatus(state, payload) { state.localHub = payload || null; },
    setLocalControlDiscovery(state, payload = {}) {
      state.localControlDiscovery = {
        ...state.localControlDiscovery,
        ...payload,
      };
    },
    setLocalControlState(state, payload = {}) {
      state.localControlState = {
        ...state.localControlState,
        ...payload,
      };
    },
    setShadowReadComparisons(state, payload) {
      state.shadowReadComparisons = Array.isArray(payload) ? payload : [];
    },
    addShadowReadComparison(state, payload) {
      state.shadowReadComparisons = [
        payload,
        ...state.shadowReadComparisons.filter(
          (item) => item.comparison_id !== payload.comparison_id,
        ),
      ].slice(0, 100);
    },
    setOperationalStatus(state, payload) {
      if (!acceptOperationalPayload(state, payload)) return;
      if (Object.prototype.hasOwnProperty.call(payload, 'primary_device')) {
        state.primaryDevice = payload.primary_device || null;
        state.serverEffectiveState = payload.primary_device?.effective_status || (payload.restaurant_has_primary ? 'UNKNOWN' : 'NO_PRIMARY');
        state.primaryAvailable = Boolean(payload.primary_device?.is_available);
        state.primaryConnected = Boolean(payload.primary_device?.is_connected);
      }
      if (payload.restaurant_operational_status) state.restaurantOperationalStatus = payload.restaurant_operational_status;
      if (payload.presence_enabled !== undefined) state.presenceEnabled = payload.presence_enabled !== false;
      state.operationalStatusCheckedAt = payload.server_time || new Date().toISOString();
      if (payload.local_hub) state.localHub = payload.local_hub;
      if (payload.current_device) {
        state.registered = Boolean(payload.current_device.registered);
        if (payload.current_device.registered) state.device = { ...(state.device || {}), ...payload.current_device };
      }
      applyOperationalServerState(state, payload);
    },
    setOperationalBlocked(state, payload = {}) {
      if (!acceptOperationalPayload(state, payload)) return;
      const context = payload.device_context || {};
      const current = context.current_device;
      const primary = context.primary_device;
      state.operationalAccessLoaded = true;
      state.operationalAccessAllowed = false;
      state.blockedByDevicePolicy = true;
      state.operationalAccessReason = payload.reason || payload.code || 'PRIMARY_UNKNOWN';
      state.blockingError = payload;
      state.operationalAccessCheckedAt = new Date().toISOString();
      if (current) {
        state.registered = current.registered !== false;
        state.device = { ...(state.device || {}), ...current };
        state.currentDeviceIsPrimary = Boolean(current.is_primary);
      }
      if (primary) {
        state.primaryDevice = { ...(state.primaryDevice || {}), ...primary };
        state.primaryEffectiveStatus = primary.effective_status || state.primaryEffectiveStatus;
      }
      persistOperationalPolicy(state);
    },
    setSecondaryApiUnavailable(state, error = null) {
      const hybridCloudFirst = state.continuityPolicy === 'HYBRID_CLOUD_FIRST';
      if (!hybridCloudFirst && (!state.secondaryBlockingEnabled || state.device?.is_primary)) return;
      const now = Date.now();
      if (!state.secondaryApiUnavailableSince) {
        state.secondaryApiUnavailableSince = now;
        return;
      }
      if (now - state.secondaryApiUnavailableSince < 60000) return;
      state.operationalAccessLoaded = true;
      state.operationalAccessAllowed = false;
      state.blockedByDevicePolicy = true;
      state.operationalAccessReason = hybridCloudFirst
        ? 'HYBRID_CLOUD_UNAVAILABLE'
        : 'SECONDARY_API_UNAVAILABLE';
      state.blockingError = error ? {
        detail: hybridCloudFirst
          ? 'No se puede conectar con EWU y el servidor local todavía no está disponible para asumir la operativa.'
          : error.message || String(error),
        code: state.operationalAccessReason,
      } : null;
      state.operationalAccessCheckedAt = new Date().toISOString();
    },
    clearOperationalBlocking(state) {
      state.secondaryApiUnavailableSince = null;
      state.operationalAccessAllowed = true;
      state.blockedByDevicePolicy = false;
      state.operationalAccessReason = null;
      state.blockingError = null;
    },
    setError(state, value) { state.error = value; state.loaded = true; },
    setPartnerContext(state, payload = {}) {
      applyOperationalServerState(state, {
        device_policy: payload.device_policy,
        local_hub: payload.local_hub,
        current_device: payload.current_device,
        primary_device: payload.device_context?.primary_device,
        restaurant_has_primary: payload.restaurant_has_primary_device,
        server_time: new Date().toISOString(),
      });
    },
    resetForPartnerSession(state) {
      stopDeviceHeartbeat();
      const identity = {
        deviceId: state.deviceId,
        identityLoaded: state.identityLoaded,
      };
      Object.assign(state, {
        ...initialOperationalState(),
        ...identity,
        loaded: false,
        registered: false,
        device: null,
        restaurantDevices: [],
        restaurantHasPrimaryDevice: false,
        userIsPartner: false,
        presenceEnabled: false,
        deviceKeyPresent: false,
        heartbeatStarted: false,
        heartbeatLeader: false,
        lastHeartbeatSentAt: null,
        lastHeartbeatSucceededAt: null,
        heartbeatError: null,
        localReportedState: 'UNKNOWN',
        serverEffectiveState: 'UNKNOWN',
        primaryDevice: null,
        restaurantOperationalStatus: 'UNKNOWN',
        primaryAvailable: false,
        primaryConnected: false,
        operationalStatusCheckedAt: null,
        error: null,
      });
    },
  },
  actions: {
    async initializeDeviceIdentity({ commit }) {
      const deviceId = await getOrCreateDeviceId();
      commit('setIdentity', deviceId);
      return deviceId;
    },
    async loadCurrentDevice({ dispatch, commit, rootState, state }) {
      try {
        const deviceId = state.deviceId || await dispatch('initializeDeviceIdentity');
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/current`, {
          ...authConfig(rootState),
          params: { device_uuid: deviceId },
        });
        commit('setCurrent', response.data);
        commit('setDeviceKeyPresent', Boolean(await getDeviceKey(deviceId)));
        if (response.data?.device && !response.data.device.is_enabled) stopDeviceHeartbeat();
        return response.data;
      } catch (error) {
        commit('setError', errorMessage(error));
        throw error;
      }
    },
    async registerCurrentDevice({ dispatch, commit, rootState, state }, name) {
      try {
        const deviceId = state.deviceId || await dispatch('initializeDeviceIdentity');
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/register`, {
          device_uuid: deviceId,
          name,
        }, authConfig(rootState));
        if (response.data?.device_key) {
          await storeDeviceKey(deviceId, response.data.device_key);
          commit('setDeviceKeyPresent', true);
        }
        commit('applyMutation', response.data);
        await dispatch('initializeDevicePresence');
        return response.data;
      } catch (error) {
        commit('setError', errorMessage(error));
        throw error;
      }
    },
    async loadRestaurantDevices({ commit, rootState }) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices`, authConfig(rootState));
        commit('setDevices', response.data);
        return response.data;
      } catch (error) {
        commit('setError', errorMessage(error));
        throw error;
      }
    },
    async makeDevicePrimary({ commit, rootState }, deviceId) {
      const response = await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/${deviceId}/make-primary`, {}, authConfig(rootState));
      commit('applyMutation', response.data);
      return response.data;
    },
    async renameDevice({ commit, rootState }, { deviceId, name }) {
      const response = await axios.patch(`${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/${deviceId}`, { name }, authConfig(rootState));
      commit('applyMutation', response.data);
      return response.data;
    },
    async removeDevice({ commit, rootState, state }, deviceId) {
      const response = await axios.delete(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/${deviceId}`,
        authConfig(rootState),
      );
      const removed = response.data?.device;
      if (
        removed
        && state.deviceId
        && String(removed.device_uuid) === String(state.deviceId)
      ) {
        stopDeviceHeartbeat();
        try {
          await clearDeviceKey(state.deviceId);
        } catch {
          // The server-side removal is already complete; a local credential
          // cleanup failure must not report the operation as failed.
        }
      }
      commit('removeDevice', response.data);
      return response.data;
    },
    async issueCurrentDeviceKey({ dispatch, commit, rootState, state }) {
      const deviceId = state.deviceId || await dispatch('initializeDeviceIdentity');
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/current/issue-key`,
        {},
        authConfig(rootState),
      );
      await storeDeviceKey(deviceId, response.data.device_key);
      commit('setDeviceKeyPresent', true);
      await dispatch('loadCurrentDevice');
      await dispatch('initializeDevicePresence');
      return response.data;
    },
    async loadLocalHubStatus({ commit, rootState }) {
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/status`,
        authConfig(rootState),
      );
      commit('setLocalHubStatus', response.data);
      return response.data;
    },
    async loadLocalHubDownload({ rootState }) {
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/v1/api/partner/local-hub/download-url`,
        authConfig(rootState),
      );
      return response.data;
    },
    async createLocalHubEnrollmentCode({ rootState }) {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/enrollment-code`,
        {},
        authConfig(rootState),
      );
      return response.data;
    },
    async requestLocalHubRefresh({ dispatch, rootState }) {
      await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/request-refresh`, {}, authConfig(rootState));
      return dispatch('loadLocalHubStatus');
    },
    async requestLocalHubIncremental({ dispatch, rootState }) {
      await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/request-incremental`, {}, authConfig(rootState));
      return dispatch('loadLocalHubStatus');
    },
    async requestLocalHubValidation({ dispatch, rootState }) {
      await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/request-validation`, {}, authConfig(rootState));
      return dispatch('loadLocalHubStatus');
    },
    async requestLocalHubBootstrap({ dispatch, rootState }) {
      await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/request-bootstrap`, {}, authConfig(rootState));
      return dispatch('loadLocalHubStatus');
    },
    async requestLocalHubRepair({ dispatch, rootState }, module) {
      await axios.post(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/request-repair`,
        { module },
        authConfig(rootState),
      );
      return dispatch('loadLocalHubStatus');
    },
    async revokeLocalHub({ dispatch, rootState }) {
      await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/revoke`, {}, authConfig(rootState));
      return dispatch('loadLocalHubStatus');
    },
    async rotateLocalHubCredential({ dispatch, rootState }) {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/local-hubs/rotate-credential`,
        {},
        authConfig(rootState),
      );
      await dispatch('loadLocalHubStatus');
      return response.data;
    },
    async initializeDevicePresence({ dispatch, state }, storeInstance = null) {
      if (!state.identityLoaded) await dispatch('initializeDeviceIdentity');
      if (!state.loaded) {
        try { await dispatch('loadCurrentDevice'); } catch { return false; }
      }
      if (!state.presenceEnabled || !state.registered || !state.device?.is_enabled) {
        return startDeviceHeartbeat(storeInstance);
      }
      const hasKey = Boolean(await getDeviceKey(state.deviceId));
      if (!hasKey) return startDeviceHeartbeat(storeInstance);
      return startDeviceHeartbeat(storeInstance);
    },
    startHeartbeat({ dispatch }) {
      return dispatch('initializeDevicePresence');
    },
    stopHeartbeat() {
      stopDeviceHeartbeat();
    },
    async refreshOperationalStatus({ commit, rootState }) {
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/operational-status`,
        authConfig(rootState),
      );
      const payload = {
        ...response.data,
        [OPERATIONAL_REQUEST_ID]: response.config?.[OPERATIONAL_REQUEST_ID],
      };
      commit('setOperationalStatus', payload);
      return payload;
    },
    async initializeOperationalAccess({ dispatch }) {
      return dispatch('refreshOperationalAccess');
    },
    async refreshOperationalAccess({ commit, rootState, state }) {
      const wasBlocked = state.blockedByDevicePolicy;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_URL}/v1/api/tpv/devices/operational-status`,
          authConfig(rootState),
        );
        const payload = {
          ...response.data,
          [OPERATIONAL_REQUEST_ID]: response.config?.[OPERATIONAL_REQUEST_ID],
        };
        commit('setOperationalStatus', payload);
        if (wasBlocked && !state.blockedByDevicePolicy && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('ewu-operational-access-restored'));
        }
        return payload;
      } catch (error) {
        if (isBackendUnavailableError(error)) commit('setSecondaryApiUnavailable', error);
        throw error;
      }
    },
    handleOperationalBlockingResponse({ commit }, payload) {
      commit('setOperationalBlocked', payload);
    },
    clearOperationalBlocking({ commit }) {
      commit('clearOperationalBlocking');
    },
    sendHeartbeatNow() {
      return sendDeviceHeartbeatNow({ forceLeadership: true });
    },
    updateLocalReportedState({ commit }, state) {
      commit('setLocalReportedState', state);
    },
  },
};

function applyOperationalServerState(state, payload = {}) {
  state.secondaryApiUnavailableSince = null;
  const policyValue = payload.device_policy?.secondary_blocking_enabled;
  if (policyValue !== undefined) state.secondaryBlockingEnabled = Boolean(policyValue);
  if (payload.device_policy?.continuity_policy) state.continuityPolicy = payload.device_policy.continuity_policy;
  if (payload.device_policy?.restaurant_operation_mode) state.restaurantOperationMode = payload.device_policy.restaurant_operation_mode;
  if (payload.device_policy?.operation_epoch) state.operationEpoch = payload.device_policy.operation_epoch;
  if (payload.device_policy?.local_control_enabled !== undefined) {
    state.localControlEnabled = Boolean(payload.device_policy.local_control_enabled);
  }
  if (payload.local_hub) state.localHub = payload.local_hub;

  const current = payload.current_device;
  const primary = payload.primary_device;
  if (current) {
    state.registered = Boolean(current.registered);
    if (current.registered) state.device = { ...(state.device || {}), ...current };
  }
  if (primary !== undefined) state.primaryDevice = primary || null;
  state.currentDeviceIsPrimary = Boolean(state.device?.is_primary);
  state.primaryEffectiveStatus = primary?.effective_status
    || payload.primary?.effective_status
    || (payload.restaurant_has_primary === false ? 'NO_PRIMARY' : state.serverEffectiveState || 'UNKNOWN');
  state.operationalAccessLoaded = true;
  state.operationalAccessCheckedAt = payload.server_time || new Date().toISOString();

  let allowed = true;
  let reason = 'DEVICE_POLICY_DISABLED';
  if (state.secondaryBlockingEnabled || state.continuityPolicy === 'HYBRID_CLOUD_FIRST') {
    if (!state.registered) {
      // Cloud mode is the source of truth for online operational writes.
      // Device registration is still needed for heartbeat/offline authority,
      // but it must not prevent a POS terminal from adding a product online.
      allowed = state.restaurantOperationMode === 'CLOUD_MODE';
      reason = allowed
        ? 'CLOUD_AVAILABLE_UNREGISTERED_DEVICE'
        : 'DEVICE_NOT_REGISTERED';
    } else if (!state.device?.is_enabled) {
      allowed = false;
      reason = 'DEVICE_REVOKED';
    } else if (state.device?.credential_valid === false) {
      allowed = false;
      reason = 'DEVICE_CREDENTIAL_INVALID';
    } else if (state.restaurantOperationMode !== 'CLOUD_MODE') {
      allowed = false;
      reason = state.restaurantOperationMode === 'LOCAL_MODE'
        ? 'RESTAURANT_LOCAL_MODE_ACTIVE'
        : 'RESTAURANT_OPERATION_MODE_CONFLICT';
    } else if (state.continuityPolicy === 'HYBRID_CLOUD_FIRST') {
      reason = 'CLOUD_AVAILABLE_DEVICE_AUTHORIZED';
    } else if (state.device?.is_primary) {
      reason = 'ALLOWED_PRIMARY_DEVICE';
    } else {
      // A successful operational-status response means cloud is reachable.
      // In CLOUD_MODE a secondary may continue operating independently of
      // the primary heartbeat; primary availability only matters to the
      // legacy browser offline path.
      reason = 'CLOUD_AVAILABLE_DEVICE_AUTHORIZED';
    }
  }
  state.operationalAccessAllowed = allowed;
  state.blockedByDevicePolicy = !allowed;
  state.operationalAccessReason = reason;
  if (allowed) state.blockingError = null;
  persistOperationalPolicy(state);
}
